import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ArrowUpRight } from "lucide-react";

export default function AuthPage() {
  const [params] = useSearchParams();
  const [mode, setMode] = useState(params.get("mode") === "register" ? "register" : "login");
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const { applyAuth } = useAuth();
  const navigate = useNavigate();

  const isRegister = mode === "register";

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const path = isRegister ? "/auth/register" : "/auth/login";
      const payload = isRegister ? form : { email: form.email, password: form.password };
      const { data } = await api.post(path, payload);
      applyAuth(data);
      toast.success(isRegister ? "Account created" : "Welcome back");
      navigate("/survey");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grain flex flex-col">
      <div className="max-w-7xl w-full mx-auto px-5 md:px-10 h-20 flex items-center relative z-10">
        <Link to="/" className="flex items-center gap-2.5">
          <img src="/scorecaster-logo.png" alt="Scorecaster" className="h-14 w-auto" />
          <span className="font-display font-extrabold text-2xl text-ink">Scorecaster</span>
        </Link>
      </div>

      <div className="flex-1 flex items-center justify-center px-5 py-10 relative z-10">
        <div className="w-full max-w-md fade-up card-soft p-8 md:p-10">
          <span className="label-tiny">{isRegister ? "Create account" : "Welcome back"}</span>
          <h1 className="font-display font-extrabold text-3xl md:text-4xl tracking-tight mt-3 mb-8 text-ink">
            {isRegister ? "Start scoring your future." : "Log in to Scorecaster."}
          </h1>

          <form onSubmit={submit} className="space-y-4">
            {isRegister && (
              <div>
                <label className="label-tiny">Full name</label>
                <input
                  data-testid="auth-name"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="mt-2 w-full bg-oat border border-clay rounded-2xl px-4 py-3 text-sm text-ink outline-none focus:border-[#DDA15E] transition-colors duration-300"
                  placeholder="Jordan Lee"
                />
              </div>
            )}
            <div>
              <label className="label-tiny">Email</label>
              <input
                data-testid="auth-email"
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="mt-2 w-full bg-oat border border-clay rounded-2xl px-4 py-3 text-sm text-ink outline-none focus:border-[#DDA15E] transition-colors duration-300"
                placeholder="you@email.com"
              />
            </div>
            <div>
              <label className="label-tiny">Password</label>
              <input
                data-testid="auth-password"
                type="password"
                required
                minLength={6}
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="mt-2 w-full bg-oat border border-clay rounded-2xl px-4 py-3 text-sm text-ink outline-none focus:border-[#DDA15E] transition-colors duration-300"
                placeholder="••••••••"
              />
            </div>
            <button
              data-testid="auth-submit"
              disabled={loading}
              className="group w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-brand-red text-white font-semibold hover:bg-[#e63f3f] transition-colors duration-300 disabled:opacity-60"
            >
              {loading ? "Please wait…" : isRegister ? "Create account" : "Log in"}
              {!loading && <ArrowUpRight size={18} />}
            </button>
          </form>

          <p className="text-sm text-stone mt-6">
            {isRegister ? "Already have an account?" : "New to Scorecaster?"}{" "}
            <button
              data-testid="auth-toggle"
              onClick={() => setMode(isRegister ? "login" : "register")}
              className="text-brand-orange font-semibold hover:underline"
            >
              {isRegister ? "Log in" : "Create one"}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
